import * as React from 'react';
import { createWorkWeekDaysArray } from 'owa-datetime-utils';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import type { ExpandableDatePickerProps } from '../schema';
import { goToTodayButtonText } from '../calendar.locstring.json';
import { Timestrip } from 'owa-timestrip';
import { isDayGridViewType } from '../utils/calendarViewTypeUtils';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { observer } from 'mobx-react-lite';
import useExpandableDatePickerViewState from '../hooks/useExpandableDatePickerViewState';
import { Label } from '@fluentui/react/lib/Label';
import { getIsDarkTheme } from 'owa-fabric-theme';

import * as styles from './ExpandableDatePicker.scss';

import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export default observer(function ExpandableDatePicker(props: ExpandableDatePickerProps) {
    const {
        onClickToday,
        fluentDateRangeType,
        workDays,
        dateRange,
        onDateRangeChange,
        onNextClick,
        onPrevClick,
        shouldShowTodayButtonText,
        datePickerRef,
        dateRangeStripText,
        dateRangeRef,
        navButtonsRef,
        timestripMaxWidth,
        prevDateRangeLabel,
        nextDateRangeLabel,
        showNavigation,
        firstDayOfWeek,
        showWeekNumbers,
        firstWeekOfYear,
        goToTodayButtonLabel,
    } = useExpandableDatePickerViewState(props);

    let pickerRowClass, navigationButtonClass;

    if (getIsDarkTheme()) {
        pickerRowClass = styles.expandableDatePickerRowDark;
        navigationButtonClass = styles.navButtonsDark;
    } else {
        pickerRowClass = styles.expandableDatePickerRowLight;
        navigationButtonClass = styles.navButtonsLight;
    }

    return (
        <div ref={datePickerRef} className={pickerRowClass}>
            <div
                ref={dateRangeRef}
                className={styles.timeStrip}
                style={{ maxWidth: timestripMaxWidth }}>
                {showNavigation ? (
                    <Timestrip
                        applySelectionStyling={true}
                        className={classNames(styles.timestripButton, navigationButtonClass)}
                        dateRange={dateRange}
                        dateRangeType={fluentDateRangeType}
                        weekStartDay={firstDayOfWeek}
                        showWeekNumbers={showWeekNumbers}
                        firstWeekOfYear={firstWeekOfYear}
                        workDays={createWorkWeekDaysArray(workDays)}
                        numDaysInDayRange={props.numDaysInDayView}
                        useWeekDay={false}
                        onDateRangeChange={onDateRangeChange}
                        customDateDisplayString={dateRangeStripText}
                        menuIconClassName={styles.canvasChevron}
                        buttonLabelClassName={styles.canvasButtonLabel}
                    />
                ) : (
                    <Label className={styles.timestripHeader}>{dateRangeStripText}</Label>
                )}
            </div>

            {showNavigation && (
                <div ref={navButtonsRef} className={styles.navigationButtons}>
                    <DefaultButton
                        ariaLabel={goToTodayButtonLabel}
                        title={goToTodayButtonLabel}
                        iconProps={{ iconName: ControlIcons.GotoToday }}
                        styles={{ label: styles.todayButtonText, icon: styles.todayButtonIcon }}
                        className={classNames(styles.todayButton, navigationButtonClass)}
                        onClick={onClickToday}>
                        {shouldShowTodayButtonText && loc(goToTodayButtonText)}
                    </DefaultButton>
                    <DefaultButton
                        ariaLabel={prevDateRangeLabel}
                        title={prevDateRangeLabel}
                        iconProps={
                            isDayGridViewType(props.viewType)
                                ? {
                                      iconName: isCurrentCultureRightToLeft()
                                          ? ControlIcons.Forward
                                          : ControlIcons.Back,
                                  }
                                : { iconName: ControlIcons.Up }
                        }
                        className={classNames(styles.backButton, navigationButtonClass)}
                        onClick={onPrevClick}
                    />
                    <DefaultButton
                        ariaLabel={nextDateRangeLabel}
                        title={nextDateRangeLabel}
                        iconProps={
                            isDayGridViewType(props.viewType)
                                ? {
                                      iconName: isCurrentCultureRightToLeft()
                                          ? ControlIcons.Back
                                          : ControlIcons.Forward,
                                  }
                                : { iconName: ControlIcons.Down }
                        }
                        className={classNames(styles.forwardButton, navigationButtonClass)}
                        onClick={onNextClick}
                    />
                </div>
            )}
        </div>
    );
});
